import { useState } from 'react';
import {
  ImportControlModeTemplateErrorDto,
  ValidationErrorItem,
} from '@src/types/ControlModes';
import { Schema, ValidationError } from 'yup';
import {
  ErrorConfig,
  ErrorType,
  PathProcessorConfig,
  PathSegmentResult,
  UseTemplateValidationResult,
} from '@src/types/hooks';

export interface FileValidationConfig<T> {
  schema: Schema<T>;
  entityName: string;
  arrayProcessors: Record<
    string,
    (
      currentObject: Record<string, unknown> | unknown[],
      index: number,
      arrayName?: string,
    ) => PathSegmentResult
  >;
  typeGuards: {
    createStringPropertyTypeGuard: <T extends string>(
      propertyName: T,
    ) => (value: unknown) => value is { [K in T]: string };
    createArrayPropertyTypeGuard: <T extends string>(
      propertyName: T,
    ) => (value: unknown) => value is { [K in T]: unknown[] };
    isObjectWithProperty: (
      value: unknown,
      propertyName: string,
    ) => value is Record<string, unknown>;
  };
}

export default function useFileValidation<T>(
  config: FileValidationConfig<T>,
): UseTemplateValidationResult {
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState<ImportControlModeTemplateErrorDto | null>(
    null,
  );

  const resetError = () => {
    setError(null);
  };

  const { isObjectWithProperty } = config.typeGuards;

  const processGenericArray = (
    currentObject: Record<string, unknown> | unknown[],
    arrayName: string,
    index: number,
  ): PathSegmentResult => {
    if (
      isObjectWithProperty(currentObject, arrayName) &&
      Array.isArray(currentObject[arrayName])
    ) {
      const array = currentObject[arrayName] as unknown[];
      const item = array[index];

      return {
        humanPart: `${arrayName}[${index}]`,
        nextObject: item as Record<string, unknown> | undefined,
      };
    } else {
      return {
        humanPart: `${arrayName}[${index}]`,
        nextObject: undefined,
      };
    }
  };

  const processPropertySegment = (
    segment: string,
    currentObject: Record<string, unknown> | unknown[],
  ): PathSegmentResult => {
    const arrayMatch = segment.match(/^([^[]*)\[(\d+)\]$/);

    if (arrayMatch) {
      const [, arrayName, indexStr] = arrayMatch;
      const index = parseInt(indexStr, 10);

      // Use the provided array processors or fall back to generic
      const processor =
        config.arrayProcessors[arrayName] ||
        ((obj: Record<string, unknown> | unknown[], idx: number) =>
          processGenericArray(obj, arrayName, idx));

      return processor(currentObject, index, arrayName);
    } else {
      if (isObjectWithProperty(currentObject, segment)) {
        return {
          humanPart: segment,
          nextObject: currentObject[segment] as
            | Record<string, unknown>
            | unknown[],
        };
      } else {
        return {
          humanPart: segment,
          nextObject: undefined,
        };
      }
    }
  };

  const processSegment = (
    segment: string,
    currentObject: Record<string, unknown> | unknown[],
    pathConfig: PathProcessorConfig,
  ): PathSegmentResult => {
    const arrayMatch = segment.match(/^([^[]*)\[(\d+)\]$/);

    if (arrayMatch) {
      const [, arrayName, indexStr] = arrayMatch;
      const index = parseInt(indexStr, 10);

      const processor =
        pathConfig.arrayProcessors[arrayName] ||
        ((obj: Record<string, unknown> | unknown[], idx: number) =>
          pathConfig.defaultArrayProcessor(obj, arrayName, idx));

      return processor(currentObject, index, arrayName);
    } else {
      return pathConfig.propertyProcessor(segment, currentObject);
    }
  };

  const processPath = (
    path: string,
    data: unknown,
    pathConfig: PathProcessorConfig,
  ): string => {
    const segments = path.split('.');
    const humanPathParts: string[] = [];

    let currentObject: Record<string, unknown> | unknown[] = data as
      | Record<string, unknown>
      | unknown[];

    for (const segment of segments) {
      if (!segment) {
        continue;
      }

      const result = processSegment(segment, currentObject, pathConfig);

      humanPathParts.push(result.humanPart);

      if (result.nextObject === undefined) {
        break;
      }
      currentObject = result.nextObject;
    }

    return humanPathParts.length > 0
      ? `at ${humanPathParts.join(' > ')}`
      : `at path "${path}"`;
  };

  const getHumanReadablePath = (path: string, data: unknown): string => {
    if (!path) {
      return '';
    }
    if (!data || typeof data !== 'object') {
      return `at path "${path}"`;
    }

    try {
      const pathConfig: PathProcessorConfig = {
        arrayProcessors: config.arrayProcessors,
        defaultArrayProcessor: processGenericArray,
        propertyProcessor: processPropertySegment,
      };

      // Process the path using the configuration
      return processPath(path, data, pathConfig);
    } catch {
      // If anything goes wrong, return the original path
      return `at path "${path}"`;
    }
  };

  const errorHandlers: Record<ErrorType, ErrorConfig> = {
    [ErrorType.VALIDATION]: {
      type: ErrorType.VALIDATION,
      title: `${config.entityName} validation failed`,
      getDetails: (err, data) => {
        if (err instanceof ValidationError) {
          return err.inner.map((innerError) =>
            formatSingleValidationError(innerError, data),
          );
        }
        return 'Unknown validation error';
      },
    },
    [ErrorType.JSON_PARSING]: {
      type: ErrorType.JSON_PARSING,
      title: 'Invalid JSON format',
      getDetails: () => 'The file does not contain valid JSON',
    },
    [ErrorType.FILE_READING]: {
      type: ErrorType.FILE_READING,
      title: 'File reading error',
      getDetails: (err) =>
        err instanceof Error ? err.message : 'Unknown error',
    },
    [ErrorType.GENERIC]: {
      type: ErrorType.GENERIC,
      title: `Invalid ${config.entityName.toLowerCase()} format`,
      getDetails: (err) =>
        err instanceof Error ? err.message : 'Unknown error',
    },
  };

  const formatSingleValidationError = (
    innerError: ValidationError,
    data: unknown,
  ): ValidationErrorItem => {
    const humanReadablePath = getHumanReadablePath(innerError.path || '', data);
    return {
      message: `An instance of ${innerError.type || 'unknown'} ${innerError.message}`,
      path: humanReadablePath,
    };
  };

  const handleError = (
    errorType: ErrorType,
    err?: unknown,
    data?: unknown,
    filePath?: string,
  ): void => {
    const errorConfig = errorHandlers[errorType];
    const details = errorConfig.getDetails(err, data);

    setError({
      statusCode: 400,
      message: errorConfig.title,
      errors: details,
      path: filePath || '/validate',
    });

    setIsValidating(false);
  };

  const validateData = async (
    data: unknown,
    filePath?: string,
  ): Promise<boolean> => {
    setIsValidating(true);
    resetError();

    try {
      await config.schema.validate(data, { abortEarly: false });

      setIsValidating(false);
      return true;
    } catch (err) {
      if (err instanceof ValidationError) {
        handleError(ErrorType.VALIDATION, err, data, filePath);
      } else {
        handleError(ErrorType.GENERIC, err, undefined, filePath);
      }
      return false;
    }
  };

  const validateFile = async (file: File): Promise<boolean> => {
    setIsValidating(true);
    resetError();

    const filePath = file.name;

    try {
      const fileContent = await readFileAsText(file);

      let parsedData;
      try {
        parsedData = JSON.parse(fileContent);
      } catch (parseError) {
        handleError(ErrorType.JSON_PARSING, parseError, undefined, filePath);
        return false;
      }

      return await validateData(parsedData, filePath);
    } catch (err) {
      handleError(ErrorType.FILE_READING, err, undefined, filePath);
      return false;
    } finally {
      if (isValidating) {
        setIsValidating(false);
      }
    }
  };

  const readFileAsText = (file: File): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        if (typeof reader.result === 'string') {
          resolve(reader.result);
        } else {
          reject(
            new Error('File read error: FileReader did not return a string'),
          );
        }
      };

      reader.onerror = () => {
        reject(new Error('File read error: Error reading file'));
      };

      reader.readAsText(file);
    });
  };

  return {
    validateTemplate: validateData,
    validateTemplateFile: validateFile,
    isValidating,
    error,
    resetError,
  };
}
